import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHero, PageCTA, Reveal } from '../../components/editorial/PageElements';
import '../../styles/FrequentlyAskedQuestions.css';

export default function Faq() {
  const [expandedItems, setExpandedItems] = useState({});
  const [category, setCategory] = useState('All questions');
  const [query, setQuery] = useState('');
  const faqData = [
    {
      category: "Support & assistance",
      items: [
        {
          question: "How can I contact customer support?",
          answer:
            'You can reach us via email at <a href="mailto:info@suryagroup.us">info@suryagroup.us</a> or <a href="/schedule-meeting">schedule a meeting</a> during business hours.',
        },
        {
          question: "What is the typical response time for support inquiries?",
          answer: "Our team strives to respond within 24 hours to all inquiries.",
        },
        {
          question: "Do you offer live chat support?",
          answer:
            "Yes, live chat support is available during working hours through our website.",
        },
        {
          question: "What kind of technical support is provided?",
          answer:
            "We help with account setup, platform navigation, and resolving technical issues.",
        },
        {
          question: "Is multilingual support available?",
          answer: "Yes, we provide support in English to cater to a diverse clientele.",
        },
      ],
    },
    {
      category: "Platform & updates",
      items: [
        {
          question: "What are the upcoming features of Surya Group's platform?",
          answer:
            "We continuously work on improving our platform. Stay tuned for updates and new features tailored for construction professionals.",
        },
        {
          question: "Will Surya Group expand its services beyond estimation?",
          answer:
            "Yes, we aim to broaden our service offerings to include more project management solutions.",
        },
        {
          question: "How can I suggest new features or improvements?",
          answer:
            "We value your feedback! Please get in touch with us through our feedback form or email to share your suggestions.",
        },
        {
          question: "Can I access training to use the new features?",
          answer:
            "Absolutely! We'll provide training materials and dedicated support for all updates.",
        },
      ],
    },
    {
      category: "Payments & billing",
      items: [
        {
          question: "What payment methods do you accept?",
          answer:
            "We accept credit/debit cards, and digital payments through popular platforms.",
        },
        {
          question: "Are there any discounts for long-term contracts?",
          answer:
            "Yes, we offer competitive discounts for long-term partnerships. Please contact us for details.",
        },
        {
          question: "Is there a refund policy?",
          answer:
            'Yes, refunds are processed as per our cancellation and refund policy. For more, <a href="/policy#refund_policy">click here</a>.',
        },
        {
          question: "Do you offer payment plans?",
          answer:
            "Yes, flexible payment options are available for larger projects.",
        },
        {
          question: "How do I receive invoices for payments?",
          answer: "Invoices will be sent to your registered email.",
        },
      ],
    },
    {
      category: "Account & collaboration",
      items: [
        {
          question: "Do I need to register to use the Surya Group platform?",
          answer:
            "Yes, registration is mandatory to access the platform. Separate accounts are available for client users (Subcontractors, General Contractors, Builders, Project Managers) and admin users.",
        },
        {
          question:
            "Can I add other team members from my company to the platform?",
          answer:
            "Yes, registered clients can invite and add other users from their company to collaborate on projects.",
        },
        {
          question: "What are the differences between client and admin user roles?",
          answer:
            "Client users can view and manage their projects, send bids, and track progress, while admin users oversee platform management and provide support.",
        },
        {
          question: "How do I recover my account if I forget my login credentials?",
          answer:
            'You can reset your password using the "Forgot Password" option on the login page or contact support for help.',
        },
        {
          question:
            "Can multiple users from the same company work on the platform simultaneously?",
          answer:
            "Yes, our platform supports multiple users from the same company working collaboratively in real time.",
        },
        {
          question:
            "How do I update my account details or permissions for team members?",
          answer:
            "You can manage your account and permissions for added users through your profile settings.",
        },
        {
          question: "What should I do if I meet an error while adding a user?",
          answer:
            "Please ensure all required fields are correctly filled. If the issue persists, contact our support team for help.",
        },
      ],
    },
  ];

  const results = faqData.map((group, groupIndex) => ({ ...group, groupIndex,
    items: group.items.map((item, index) => ({ ...item, id: `${groupIndex}-${index}` })).filter(item => `${item.question} ${item.answer.replace(/<[^>]*>/g, '')}`.toLowerCase().includes(query.trim().toLowerCase()))
  })).filter(group => (category === 'All questions' || group.category === category) && group.items.length);
  const count = results.reduce((total, group) => total + group.items.length, 0);
  return <div className="editorial-page faq-page">
    <PageHero page="FAQ" eyebrow="THE HELP DESK" title="Good questions." accent="Clear answers." description="Everything you need to know about working with Surya Group, from your first conversation to ongoing collaboration." />
    <div className="editorial-container editorial-section">
      <div className="faq-layout">
        <aside className="faq-sidebar"><span className="editorial-eyebrow">EXPLORE BY TOPIC</span><nav aria-label="FAQ categories">{['All questions', ...faqData.map(group => group.category)].map(label => <button type="button" key={label} aria-pressed={category === label} onClick={() => setCategory(label)}>{label}<span aria-hidden="true">↗</span></button>)}</nav><div className="faq-help"><h2>Prefer a conversation?</h2><p>Our team is here to help with your specific questions.</p><Link to="/contact-us">Get in touch <span aria-hidden="true">→</span></Link></div></aside>
        <div className="faq-results"><label className="faq-search-label" htmlFor="faq-search">Find your answer</label><div className="faq-search"><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="10" cy="10" r="6"/><path d="m15 15 6 6"/></svg><input id="faq-search" type="search" placeholder="Search questions, payments, accounts…" value={query} onChange={e => setQuery(e.target.value)} /></div><p className="faq-count" role="status">{count} {count === 1 ? 'answer' : 'answers'}{query.trim() ? ' matching your search' : ' to help you get started'}</p>
        {results.map(group => <Reveal key={group.category} className="faq-group"><h2>{group.category}</h2>{group.items.map(item => { const expanded = !!expandedItems[item.id]; return <div className={`faq-entry ${expanded ? 'is-open' : ''}`} key={item.id}><h3><button className="faq_toggle" id={`faq-question-${item.id}`} aria-expanded={expanded} aria-controls={`faq-answer-${item.id}`} onClick={() => setExpandedItems(prev => ({...prev, [item.id]: !prev[item.id]}))}>{item.question}<span className="faq-plus" aria-hidden="true">+</span></button></h3><div id={`faq-answer-${item.id}`} role="region" aria-labelledby={`faq-question-${item.id}`} hidden={!expanded} className="faq-answer"><p dangerouslySetInnerHTML={{__html: item.answer}} /></div></div>; })}</Reveal>)}
        {!count && <div className="faq-empty"><h2>No matching answers yet.</h2><p>Try a different keyword or browse all questions.</p><button className="editorial-button" onClick={() => { setQuery(''); setCategory('All questions'); }}>Clear filters</button></div>}
        </div>
      </div>
    </div><PageCTA title="Still have something on your mind?" description="Every project is different. Our team can help you work through the details." />
  </div>;
}
